import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

import { foldStatus } from '../fold';
import type { StediStatusView } from '../types';

import { StatusAgeLine } from './StatusAgeLine';

export interface StediStatusListProps {
  statuses: StediStatusView[];
  /** Roster cells need payer context; a payer table already supplies it in the first column. */
  showPayerName?: boolean;
  ariaLabel: string;
  /** Keep roster rows compact until staff asks to inspect every source row. */
  collapsedItemCount?: number;
  /** Reference instant for each status's age; omit to leave the age line out. */
  asOf?: Date;
}

/** Exact, unclassified Stedi values with their transaction rail, one per selected enrollment. */
export function StediStatusList({
  statuses,
  showPayerName = false,
  ariaLabel,
  collapsedItemCount,
  asOf,
}: StediStatusListProps) {
  const [expanded, setExpanded] = useState(false);
  if (statuses.length === 0) {
    return <Typography color="text.secondary">—</Typography>;
  }

  const isCollapsible =
    typeof collapsedItemCount === 'number' &&
    collapsedItemCount > 0 &&
    statuses.length > collapsedItemCount;
  const visibleStatuses =
    isCollapsible && !expanded ? statuses.slice(0, collapsedItemCount) : statuses;
  // Only label rails by NPI when the list actually spans more than one provider.
  const spansProviders =
    new Set(statuses.map(item => item.stediProviderId).filter(Boolean)).size > 1;

  return (
    <Stack
      component="ul"
      spacing={0.75}
      aria-label={ariaLabel}
      sx={{ listStyle: 'none', p: 0, m: 0, minWidth: 0, whiteSpace: 'normal' }}
    >
      {visibleStatuses.map((item, index) => (
        <Box component="li" key={`${item.enrollmentId}-${index}`} sx={{ minWidth: 0 }}>
          {showPayerName && (
            <Typography sx={{ fontSize: '12px', fontWeight: 600, lineHeight: 1.35 }}>
              {item.payerName}
            </Typography>
          )}
          <Typography
            sx={{
              color: 'text.secondary',
              fontFamily: 'monospace',
              fontSize: '12px',
              lineHeight: 1.35,
              overflowWrap: 'anywhere',
            }}
          >
            {`${item.transactionType} · ${item.status}`}
            {spansProviders && item.providerNpi ? ` · NPI ${item.providerNpi}` : ''}
          </Typography>
          {asOf && (
            <StatusAgeLine
              statusUpdatedAt={item.statusUpdatedAt}
              asOf={asOf}
              state={foldStatus(item.status)}
            />
          )}
        </Box>
      ))}
      {isCollapsible && (
        <Box component="li">
          <Button size="small" onClick={() => setExpanded(value => !value)} sx={{ px: 0 }}>
            {expanded ? 'Show fewer' : `Show all ${statuses.length} statuses`}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
